import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Blog.css';

function Blog() {
    // State for blog data
    const [blogData, setBlogData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const blogsPerPage = 12;
    const blogCardsRef = useRef(null);

    // Fetch blogs from the backend API
    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                setIsLoading(true);
                const response = await fetch('https://b.centrexmarineservices.com/Api/View.php');
                
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                
                const data = await response.json();
                
                if (data.status === 'success') {
                    setBlogData(data.data || []);
                    setError(null);
                } else {
                    setError(data.message || 'Failed to fetch blogs');
                    setBlogData([]);
                }
            } catch (err) {
                console.error('Error fetching blogs:', err);
                setError('Failed to fetch blogs. Please try again later.');
                setBlogData([]);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchBlogs();
    }, []);

    // Get current blogs
    const indexOfLastBlog = currentPage * blogsPerPage;
    const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
    const currentBlogs = blogData.slice(indexOfFirstBlog, indexOfLastBlog);

    // Change page
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll to the top of the blog cards section
        if (blogCardsRef.current) {
            blogCardsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Get pagination numbers to display (show only 5 at a time)
    const getPaginationGroup = () => {
        const totalPages = Math.ceil(blogData.length / blogsPerPage);
        
        // If total pages is 5 or less, show all pages
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        
        // Calculate the pagination group
        let startPage = Math.max(currentPage - 2, 1);
        let endPage = startPage + 4;
        
        // Adjust if endPage exceeds totalPages
        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(endPage - 4, 1);
        }
        
        return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    };

    // Format date for display
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    return (
        <div className="blog-container">
            <div className="blog-hero">
                <div className="blog-overlay">
                    <h1>Blogs</h1>
                </div>
            </div>
            
            {/* White gap */}
            <div className="white-gap"></div>
            
            {/* Blog cards section */}
            <div className="blog-cards-container" ref={blogCardsRef}>
                {isLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading blogs...</p>
                    </div>
                ) : error ? (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                ) : blogData.length === 0 ? (
                    <div className="no-blogs-message">
                        <p>No blogs found. Check back later for new content!</p>
                    </div>
                ) : (
                    currentBlogs.map(blog => (
                        <div className="blog-card" key={blog.id}>
                            <div className="blog-card-image">
                                {blog.image_path ? (
                                    <img 
                                        src={blog.image_path} 
                                        alt={blog.title}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/Pics/placeholder-image.jpg';
                                        }}
                                    />
                                ) : (
                                    <img src="/Pics/placeholder-image.jpg" alt="Placeholder" />
                                )}
                            </div>
                            <div className="blog-card-content">
                                <span className="blog-date">{formatDate(blog.date_published)}</span>
                                <h3 className="blog-title">{blog.title}</h3>
                                <p className="blog-excerpt">
                                    {blog.excerpt || blog.content.substring(0, 150) + '...'}
                                </p>
                                <Link to={`/blog/${blog.id}`} className="read-more-btn">Read More</Link>
                            </div>
                        </div>
                    ))
                )}
            </div>
            
            {/* Enhanced Pagination - only show if there are blogs and not loading */}
            {!isLoading && !error && blogData.length > blogsPerPage && (
                <div className="pagination">
                    {/* Previous button */}
                    {currentPage > 1 && (
                        <button 
                            className="pagination-nav"
                            onClick={() => paginate(currentPage - 1)}
                        >
                            &laquo;
                        </button>
                    )}
                    
                    {/* First page button if not in view */}
                    {currentPage > 3 && (
                        <>
                            <button onClick={() => paginate(1)}>1</button>
                            {currentPage > 4 && <span className="pagination-dots">...</span>}
                        </>
                    )}
                    
                    {/* Page numbers */}
                    {getPaginationGroup().map(number => (
                        <button
                            key={number}
                            onClick={() => paginate(number)}
                            className={currentPage === number ? 'active' : ''}
                        >
                            {number}
                        </button>
                    ))}
                    
                    {/* Last page button if not in view */}
                    {currentPage < Math.ceil(blogData.length / blogsPerPage) - 2 && (
                        <>
                            {currentPage < Math.ceil(blogData.length / blogsPerPage) - 3 && 
                                <span className="pagination-dots">...</span>
                            }
                            <button onClick={() => paginate(Math.ceil(blogData.length / blogsPerPage))}>
                                {Math.ceil(blogData.length / blogsPerPage)}
                            </button>
                        </>
                    )}
                    
                    {/* Next button */}
                    {currentPage < Math.ceil(blogData.length / blogsPerPage) && (
                        <button 
                            className="pagination-nav"
                            onClick={() => paginate(currentPage + 1)}
                        >
                            &raquo;
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default Blog;
